import React from "react";
import { motion } from "framer-motion";
import { TID } from "@/lib/testIds";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
function startLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const ColorBlob = ({ color, className }) => (
  <span
    className={`inline-block align-middle rounded-full border-2 border-[var(--text)] ${className}`}
    style={{ background: color, boxShadow: "3px 3px 0 0 var(--text)" }}
  />
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--text)] overflow-x-hidden">
      {/* Top strip */}
      <div className="w-full bg-[var(--red)] text-white border-b-[3px] border-[var(--text)] py-2 text-center text-sm font-bold tracking-widest uppercase">
        18+ only · Free delivery in Cape Town over R500 · Open 7 days
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[var(--cream)] border-b-[3px] border-[var(--text)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--red)] border-2 border-[var(--text)] flex items-center justify-center" style={{ boxShadow: "3px 3px 0 0 var(--text)" }}>
              <span className="font-display text-white text-lg">T</span>
            </div>
            <span className="font-display text-2xl">Tjoef</span>
          </div>
          <button
            data-testid={TID.landingLoginBtn}
            onClick={startLogin}
            className="btn-primary text-sm"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="relative">
        <section className="max-w-7xl mx-auto px-6 pt-16 pb-14 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="label-eyebrow">The corner cannabis shop · Reimagined</span>
              <h1 className="mt-6 font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.95] text-[var(--text)]">
                <span className="text-[var(--red)]">Craft cannabis</span>,{" "}
                <span className="italic font-serif-display font-black" style={{ fontFamily: "'Fraunces', serif" }}>delivered</span>
                <br />
                <span className="text-[var(--blue)]">to your</span>{" "}
                <span className="bg-[var(--yellow)] px-3 border-2 border-[var(--text)] rounded-xl inline-block -rotate-2" style={{ boxShadow: "4px 4px 0 0 var(--text)" }}>
                  stoep.
                </span>
              </h1>
              <p className="mt-8 text-lg text-[var(--text-2)] max-w-lg leading-relaxed">
                Cape Town&#39;s brightest cannabis corner shop. Flower, edibles, vapes
                &amp; pop-culture pizzazz — delivered by real humans, priced in rand.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <button
                  data-testid={TID.landingCtaBtn}
                  onClick={startLogin}
                  className="btn-primary text-base px-6 py-3"
                >
                  Start shopping · Sign in with Google
                </button>
                <a href="#how" className="btn-ghost text-sm">How it works</a>
              </div>
              <div className="mt-10 flex items-center gap-2">
                <ColorBlob color="#E63946" className="w-6 h-6" />
                <ColorBlob color="#1D3FA3" className="w-6 h-6" />
                <ColorBlob color="#FFC93C" className="w-6 h-6" />
                <ColorBlob color="#10A46C" className="w-6 h-6" />
                <ColorBlob color="#FF6B9D" className="w-6 h-6" />
                <span className="ml-3 text-xs font-bold tracking-widest uppercase text-[var(--text-2)]">
                  Real store energy
                </span>
              </div>
            </motion.div>
          </div>

          <div className="col-span-12 lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative"
            >
              <div className="card-panel p-2 rotate-2">
                <div
                  className="rounded-2xl aspect-square"
                  style={{
                    backgroundImage: "url('/hero-cartoon.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundColor: "#FFF3D6",
                  }}
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[var(--yellow)] border-2 border-[var(--text)] rounded-2xl px-5 py-3 -rotate-3" style={{ boxShadow: "4px 4px 0 0 var(--text)" }}>
                <p className="font-display text-xl">Sale on!</p>
                <p className="text-xs font-bold uppercase tracking-widest">Save up to 40%</p>
              </div>
              <div className="absolute -top-8 -right-4 bg-[var(--pink)] border-2 border-[var(--text)] rounded-full w-24 h-24 flex items-center justify-center rotate-12" style={{ boxShadow: "4px 4px 0 0 var(--text)" }}>
                <span className="font-display text-white text-2xl leading-tight text-center">NEW!</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Aisle bar */}
        <section className="border-y-[3px] border-[var(--text)] bg-[var(--blue)] py-4 overflow-hidden">
          <div className="marquee flex items-center gap-10 whitespace-nowrap animate-[wiggle_2s_ease-in-out_infinite]">
            {["FLOWER", "PRE-ROLLS", "VAPES", "EDIBLES", "CONCENTRATES", "TINCTURES", "ACCESSORIES"].map((c) => (
              <span key={c} className="font-display text-white text-3xl px-6">
                ★ {c}
              </span>
            ))}
            {["FLOWER", "PRE-ROLLS", "VAPES", "EDIBLES"].map((c, i) => (
              <span key={i + "-2"} className="font-display text-white text-3xl px-6">
                ★ {c}
              </span>
            ))}
          </div>
        </section>

        {/* How it works — 3 aisles */}
        <section id="how" className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <span className="label-eyebrow">How it works</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl">Three aisles to happy shopping.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Fill your basket", d: "Browse every aisle. R-priced. No hidden fees.", c: "var(--red)" },
              { n: "02", t: "Tap to pay", d: "Card, Apple Pay, Google Pay — safely via Stripe.", c: "var(--blue)" },
              { n: "03", t: "Track to your door", d: "Watch our driver zoom across the map in real time.", c: "var(--green)" },
            ].map((s) => (
              <div key={s.n} className="card-panel p-8 relative">
                <div
                  className="w-16 h-16 rounded-2xl border-2 border-[var(--text)] flex items-center justify-center font-display text-white text-2xl -rotate-3"
                  style={{ background: s.c, boxShadow: "4px 4px 0 0 var(--text)" }}
                >
                  {s.n}
                </div>
                <h3 className="mt-6 font-display text-2xl">{s.t}</h3>
                <p className="mt-2 text-[var(--text-2)]">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 pb-24">
          <div className="card-panel p-10 bg-dots-yellow text-center">
            <span className="label-eyebrow" style={{ color: "var(--text)" }}>Ready?</span>
            <h3 className="mt-3 font-display text-4xl sm:text-5xl">Sign in &amp; start shopping.</h3>
            <button onClick={startLogin} data-testid="landing-footer-cta" className="btn-primary mt-8 text-base">
              Continue with Google
            </button>
            <p className="mt-4 text-xs font-bold uppercase tracking-widest text-[var(--text-2)]">
              Delivering across Cape Town · Est. 2026
            </p>
          </div>
        </section>

        <footer className="border-t-[3px] border-[var(--text)] bg-[var(--red)] text-white py-6">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs font-bold tracking-widest uppercase">
            <span>© Tjoef · Cape Town</span>
            <span>Made with 🧡 in ZA</span>
          </div>
        </footer>
      </main>
    </div>
  );
}
